import React from 'react';

import Close from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import AbsorbanceTransmittanceConverter from 'client/app/apps/standalone-tools/absorbance-transmittance-converter/AbsorbanceTransmittanceConverter';
import DensityCalculator from 'client/app/apps/standalone-tools/density-calculator/DensityCalculator';
import MassMolarityCalculator from 'client/app/apps/standalone-tools/mass-molarity-calcuator/MassMolarityCalculator';
import StockDilutionCalculator from 'client/app/apps/standalone-tools/stock-dilution-calculator/StockDilutionCalculator';
import Colors from 'common/ui/Colors';

export enum StandaloneTool {
  MASS_MOLARITY,
  DENSITY,
  STOCK_DILUTION,
  ABS_TRANS_CONVERTER,
  COMING_SOON,
}

function useToolTitle(tool: StandaloneTool) {
  switch (tool) {
    case StandaloneTool.MASS_MOLARITY:
      return 'Molarity Calculator';
    case StandaloneTool.DENSITY:
      return 'Density Calculator';
    case StandaloneTool.STOCK_DILUTION:
      return 'Stock Dilution Calculator';
    case StandaloneTool.ABS_TRANS_CONVERTER:
      return 'Absorbance/Transmittance';
    case StandaloneTool.COMING_SOON:
      return 'Tool';
  }
}

function useToolComponent(tool: StandaloneTool) {
  switch (tool) {
    case StandaloneTool.MASS_MOLARITY:
      return <MassMolarityCalculator />;
    case StandaloneTool.DENSITY:
      return <DensityCalculator />;
    case StandaloneTool.STOCK_DILUTION:
      return <StockDilutionCalculator />;
    case StandaloneTool.ABS_TRANS_CONVERTER:
      return <AbsorbanceTransmittanceConverter />;
    case StandaloneTool.COMING_SOON:
      return <Typography>Coming soon!</Typography>;
  }
}

type StandaloneToolDialogueProps = {
  onClose: () => void;
  tool: StandaloneTool;
};

export default function StandaloneToolDialog({
  onClose,
  tool,
}: StandaloneToolDialogueProps) {
  const handleClose = () => {
    onClose();
  };

  const toolTitle = useToolTitle(tool);

  const toolComponent = useToolComponent(tool);

  return (
    <Dialog onClose={handleClose} open fullWidth>
      <DialogHeader>
        <ToolTitle variant="h1">{toolTitle}</ToolTitle>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogHeader>
      <StyledDialogContent>{toolComponent}</StyledDialogContent>
    </Dialog>
  );
}

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
});

const DialogHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(5, 5, 0, 5),
  display: 'grid',
  gridAutoFlow: 'column',
  gridTemplateColumns: '1fr auto',
}));

const ToolTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(3, 0, 0, 3),
  fontSize: 24,
  fontWeight: 900,
  fontFamily: 'Inter',
  lineHeight: '24px',
  '&::after': {
    content: '"."',
    color: Colors.SECONDARY_MAIN,
  },
}));
